import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import Loading from "../../app/ui/Loading/loading";
import { appConfig } from "../../config/app";

const TransactionPreview = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transaction, setTransaction] = useState(null);
  const [playerDetails, setPlayerDetails] = useState(null);

  const location = useLocation();

  // Effect to open the page in a new tab on initial load
  useEffect(() => {
    // Check if this is not a new tab (if it was opened by another page)
    if (window.opener) {
      // This means we're already in a new tab, don't open another
      return;
    }

    // Check if this is the initial render
    const isInitialRender =
      sessionStorage.getItem("transactionPageOpened") !== "true";

    if (isInitialRender) {
      // Set flag to prevent infinite loop of new tabs
      sessionStorage.setItem("transactionPageOpened", "true");

      // Open current URL in new tab
      const newTab = window.open(window.location.href, "_blank");

      // If new tab was successfully opened, close the current tab
      if (newTab) {
        window.close();
      }
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const searchParams = new URLSearchParams(location.search);
        const referenceNo = searchParams.get("ref");

        if (!referenceNo) {
          throw new Error("Cart Id not found.");
        }

        const transactionResponse = await axios.get(
          `${appConfig.DotNet_Api_Endpoint}/payments/payment_logs/reference/${referenceNo}`
        );

        const transactionData = transactionResponse.data;
        setTransaction(transactionData);

        if (transactionData && transactionData.playerId) {
          const playerResponse = await axios.get(
            `${appConfig.DotNet_Api_Endpoint}/soccer/user/players/${transactionData.playerId}`,
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          const playerData = playerResponse.data;
          setPlayerDetails(playerData);
        } else {
          throw new Error("Player ID not found in transaction data");
        }
      } catch (err) {
        setError(err.message || "Failed to load data");
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [location]);

  if (loading) return <Loading />;
  if (error) return <div className="text-center py-5 text-danger">{error}</div>;

  const cardBgColor = "bg-white";

  // const formatTransactionDate = (dateString) => {
  //   const date = new Date(dateString);
  //   return date.toLocaleString();
  // };

  // const formatExpirationDate = (dateString) => {
  //   const date = new Date(dateString);
  //   return date.toLocaleDateString();
  // };
  const formatTransactionDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return "Invalid Date";

    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    const time = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return `${day}-${month}-${year} ${time}`;
  };

  const formatExpirationDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return "Invalid Date";

    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  //0th invoice's expiration date
  const lastInvoiceExpirationDate = playerDetails?.invoices?.[0]
    ?.expiration_date
    ? formatExpirationDate(playerDetails.invoices[0].expiration_date)
    : "N/A";

  return (
    <div className="min-vh-100 bg-light d-flex flex-column">
      <div
        className="text-white py-2 px-4 d-flex align-items-center"
        style={{
          backgroundColor: "#040c4b",
          WebkitPrintColorAdjust: "exact",
          printColorAdjust: "exact",
        }}
      >
        <div className="logo-wrapper me-2">
          <img
            src="/AcademyLogoBlue.png"
            style={{
              filter: "invert(1) grayscale(1) brightness(2)",
              width: "40px",
              height: "auto",
            }}
            alt="Al Hilal Academy Logo"
          />
        </div>
        <h5 className="mb-0 fw-bold text-white">Al Hilal Academy</h5>
      </div>

      <div className="flex-grow-1 d-flex justify-content-center align-items-center pt-5 pb-4">
        <div
          className={`shadow p-4 ${cardBgColor}`}
          id="printable-receipt"
          style={{
            maxWidth: "500px",
            width: "100%",
            borderRadius: "1rem",
            backgroundColor: "#ffffff", // Ensure background is white in print
            WebkitPrintColorAdjust: "exact",
            printColorAdjust: "exact",
            padding: "2rem",
          }}
        >
          <div className="position-relative">
            {transaction?.receiptQrUrl && (
              <img
                src={transaction.receiptQrUrl}
                alt="QR Code"
                className="position-absolute"
                style={{
                  top: 0,
                  right: 0,
                  width: "80px",
                  height: "80px",
                  margin: "0rem",
                  objectFit: "contain",
                }}
              />
            )}

            <h4 className="text-center fw-bold mb-2">Transaction Receipt</h4>
          </div>{" "}
          <p className="text-center mb-4">
            <span
              className={`badge rounded-pill px-3 py-2 ${
                transaction?.status === "Success"
                  ? " text-success"
                  : " text-danger"
              }`}
              style={{ fontSize: "1.2rem", fontWeight: "600" }}
            >
              {transaction?.status}
            </span>
          </p>
          <div className="mb-3 pb-3 border-bottom">
            <div className="d-flex justify-content-between mb-2">
              <strong>Reference Number:</strong>
              <span>{transaction?.referenceNo}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <strong>Date & Time:</strong>
              <span>{formatTransactionDate(transaction?.transactionDate)}</span>
            </div>
          </div>
          <div className="mb-3 pb-3 border-bottom">
            <div className="d-flex justify-content-between mb-2">
              <strong>Player:</strong>
              <span>{transaction?.playerName}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <strong>Branch:</strong>
              <span>{transaction?.branch}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <strong>Membership:</strong>
              <span>{transaction?.membershipName}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <strong>Expiration Date:</strong>
              <span>{lastInvoiceExpirationDate}</span>
            </div>
          </div>
          <div className="mb-3 pb-3 border-bottom">
            <div className="d-flex justify-content-between mb-2">
              <strong>Payment Method:</strong>
              <span>{transaction?.paymentMethod}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <strong>Amount:</strong>
              <span className="fw-bold">
                {transaction?.amount.toFixed(2)} SAR
              </span>
            </div>
          </div>
          <div className="text-center mt-3">
            {transaction?.status === "Success" ? (
              <p className="mb-1">Thank you for your payment!</p>
            ) : (
              <p className="mb-1 text-danger">
                Payment failed. Please contact support.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="text-center mb-4 d-print-none">
        <button
          className="btn text-white"
          style={{ backgroundColor: "#040c4b" }}
          onClick={() => window.print()}
        >
          Print / Save as PDF
        </button>
      </div>

      {/* Footer */}
      <div className="text-center text-muted py-3 d-print-none">
        © 2025 Al Hilal Academy. All rights reserved.
      </div>

      {/* <style>
        {`
          @media print {
            .badge {
              border: none !important;
            }
          }
        `}
      </style> */}
      <style>
        {`
    @media print {
      body {
        background-color: #ffffff !important;
      }

      .badge {
        border: none !important;
      }

      #printable-receipt {
        box-shadow: none !important;
        border-radius: 1rem !important;
        background-color: #ffffff !important;
        max-width: 500px !important;
        width: 100% !important;
        padding: 2rem !important;
        margin: auto !important;
        transform: none !important;
      }

      .text-danger {
        color: #dc3545 !important; /* Ensure red error text is printed */
      }

      .text-success {
        color: #198754 !important;
      }

      .text-white {
        color: #ffffff !important;
      }

      .bg-danger-subtle {
        background-color: #f8d7da !important;
      }

      .bg-success-subtle {
        background-color: #d1e7dd !important;
      }

      .fw-bold {
        font-weight: 600 !important;
      }
    }
  `}
      </style>
    </div>
  );
};

export default TransactionPreview;
